import type { GridPoint } from '../core/types';
import { buildingDef, type BuildingTypeId } from '../sim/buildings/definitions';
import { originForCursor } from '../sim/buildings/footprint';
import type { PlacementResult } from '../sim/buildings/placement';
import type { CommandResult, CommandSink } from '../sim/commands';
import type { PlayerId } from '../sim/ids';
import type { Simulation } from '../sim/Simulation';

export interface BuildModeState {
  /** Building being placed, or null when not in build mode. */
  readonly active: BuildingTypeId | null;
  /** Validation of the footprint under the cursor, or null (no cursor / not active). */
  readonly preview: PlacementResult | null;
  /**
   * The preview was put down by touch (tap / drag) rather than following a
   * mouse. A pinned preview waits for an explicit confirm, and hover updates
   * (which touch never sends) cannot move it.
   */
  readonly pinned: boolean;
}

/**
 * Client-side placement mode (a UI state machine, not game state):
 *   idle ──start(type)──▶ placing ──cancel()──▶ idle
 *   placing: cursor moves → preview re-validated with the SAME rules the
 *   simulation enforces; place() → a `placeBuilding` command to the sink.
 *   Stays in placing mode after success so walls / houses can be repeated.
 *
 * Two ways to aim:
 *   mouse  — the preview follows the hover cursor; a click places.
 *   touch  — a tap or drag PINS the preview; a separate confirm places it.
 *            Moving a real mouse afterwards un-pins (hybrid devices).
 */
export class BuildMode {
  private active: BuildingTypeId | null = null;
  private cursor: GridPoint | null = null;
  private preview: PlacementResult | null = null;
  private pinned = false;
  private readonly listeners = new Set<(s: BuildModeState) => void>();

  constructor(
    private readonly sim: Simulation,
    private readonly sink: CommandSink,
    private readonly player: PlayerId,
  ) {}

  get state(): BuildModeState {
    return { active: this.active, preview: this.preview, pinned: this.pinned };
  }

  get isActive(): boolean {
    return this.active !== null;
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  subscribe(l: (s: BuildModeState) => void): () => void {
    this.listeners.add(l);
    l(this.state);
    return () => this.listeners.delete(l);
  }

  start(type: BuildingTypeId): void {
    if (!buildingDef(type).playerBuildable) throw new Error(`${type} cannot be placed by players`);
    this.active = type;
    this.revalidate();
  }

  /** Starting the active type again cancels. */
  toggle(type: BuildingTypeId): void {
    if (this.active === type) this.cancel();
    else this.start(type);
  }

  cancel(): void {
    if (this.active === null) return;
    this.active = null;
    this.preview = null;
    this.pinned = false;
    this.cursor = null;
    this.notify();
  }

  /**
   * Hover cursor tile (may be off-map → out-of-bounds preview) or null to hide
   * the preview. While pinned, null is ignored and a real tile un-pins.
   */
  setCursor(tile: GridPoint | null): void {
    if (this.pinned) {
      if (!tile) return;
      this.pinned = false;
    } else if (samePoint(tile, this.cursor)) {
      return;
    }
    this.cursor = tile ? { x: tile.x, y: tile.y } : null;
    if (this.active) this.revalidate();
  }

  /** Touch: put the preview on this tile and keep it there until moved, confirmed or cancelled. */
  pinCursor(tile: GridPoint): void {
    if (this.pinned && samePoint(tile, this.cursor)) return;
    this.pinned = true;
    this.cursor = { x: tile.x, y: tile.y };
    if (this.active) this.revalidate();
  }

  /**
   * Touch: pin the preview on the valid site nearest to `center` (rings of up
   * to `maxRadius` tiles), so a freshly chosen building starts somewhere it can
   * actually go. If the player cannot afford it, the nearest site that is fine
   * apart from the cost is used, so the preview explains what is missing.
   * Falls back to `center` (shown red with the reason).
   */
  pinNearestValid(center: GridPoint, maxRadius = 8): void {
    if (!this.active) return;
    const fp = buildingDef(this.active).footprint;
    for (let r = 0; r <= maxRadius; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const c = { x: center.x + dx, y: center.y + dy };
          const v = this.sim.validatePlacement(this.player, this.active, originForCursor(c, fp));
          if (v.ok || (v.failures.length === 1 && v.failures[0] === 'insufficient_resources')) {
            this.pinCursor(c);
            return;
          }
        }
      }
    }
    this.pinCursor(center);
  }

  /** Is this tile part of the current preview footprint? (Touch: a drag starting here moves the preview.) */
  previewCovers(tile: GridPoint): boolean {
    const p = this.preview;
    if (!p) return false;
    const fp = p.definition.footprint;
    return tile.x >= p.origin.x && tile.x < p.origin.x + fp.width && tile.y >= p.origin.y && tile.y < p.origin.y + fp.height;
  }

  /** Re-check the preview after the world changed. */
  revalidate(): void {
    this.preview =
      this.active && this.cursor ? this.sim.validatePlacement(this.player, this.active, originForCursor(this.cursor, buildingDef(this.active).footprint)) : null;
    this.notify();
  }

  /** Submit a placement command for the active building at the cursor. */
  place(): CommandResult {
    if (!this.active) return { ok: false, code: 'invalid_payload', message: 'No building selected.' };
    if (!this.cursor) return { ok: false, code: 'invalid_payload', message: 'Point at the map to place.' };
    const origin = originForCursor(this.cursor, buildingDef(this.active).footprint);
    const r = this.sink.submit({ type: 'placeBuilding', playerId: this.player, building: this.active, origin });
    this.revalidate();
    return r;
  }

  private notify(): void {
    const s = this.state;
    for (const l of this.listeners) l(s);
  }
}

function samePoint(a: GridPoint | null, b: GridPoint | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y;
}
